import { Progress } from '@backstage/core-components';
import { Typography } from '@material-ui/core';
import type {
  ControlPlaneCapability,
  ControlPlaneOperationalProjection,
} from '@internal/spassu-code-contract';
import type { ControlPlaneOnboardingView } from '../hooks/useControlPlaneOnboarding';
import {
  ConnectionSetupPanel,
  PairingPanel,
} from './ControlPlaneConnectionPanels';
import {
  CONNECTION_LABELS,
  EXPIRY_BAND_LABELS,
} from './control-plane-management-labels';
import { useConnectionPageStyles } from './useConnectionPageStyles';

/**
 * Fails CLOSED. Emitting one-use credential material needs an affirmative
 * canonical answer from the `pair` capability, not the absence of a refusal:
 * before the first load, on a failed refresh, or on a host without the API the
 * page offers nothing. The session status narrows it further, because a
 * connected session is not pairable either.
 */
function canGeneratePairing(
  view: ControlPlaneOnboardingView,
  pair?: ControlPlaneCapability,
): boolean {
  if (!pair?.allowed) return false;
  return (
    !view.session ||
    ['expired', 'revoked', 'failed'].includes(view.session.status)
  );
}

/**
 * Steady-state live-region text, from the canonical projection only. Transient
 * operation feedback (generating a code, a pairing error) still comes from the
 * hook and takes precedence while it is present. Without a projection it
 * states that the state is unavailable rather than falling back to a
 * handoff-derived claim (ADR-009).
 */
export function canonicalAnnouncement(
  projection: ControlPlaneOperationalProjection | null | undefined,
): string {
  if (!projection) return 'Estado da conexão indisponível.';
  const band =
    projection.connection.expiryBand === 'none'
      ? ''
      : ` ${EXPIRY_BAND_LABELS[projection.connection.expiryBand]}.`;
  return `Conexão: ${CONNECTION_LABELS[projection.connection.state]}.${band}`;
}

/**
 * Owner-scoped pairing surface: initial pairing (when the canonical capability
 * allows it), the one-use command already emitted in memory and the page's
 * single `role="status"` region. Revocation belongs to SessionActions.
 */
export function ConnectionSection({
  view,
  pairCapability,
  announcement,
}: {
  view: ControlPlaneOnboardingView;
  pairCapability?: ControlPlaneCapability;
  announcement: string;
}) {
  const classes = useConnectionPageStyles();
  if (view.loading) return <Progress />;
  if (!view.activity) return <ConnectionReadFailure view={view} />;
  return (
    <>
      {canGeneratePairing(view, pairCapability) && (
        <ConnectionSetupPanel
          busy={view.operation === 'pairing'}
          onGenerate={view.generate}
        />
      )}
      <PairingPanel view={view} />
      {view.error && (
        <Typography role="alert" color="error">
          {view.error}
        </Typography>
      )}
      {/*
        Transient feedback (code being generated, command copied) is painted;
        the steady canonical text is announced only, because the chip above
        already shows the same state and a second copy read as noise.
      */}
      <Typography
        role="status"
        aria-live="polite"
        aria-atomic="true"
        variant="body2"
        className={view.announcement ? classes.liveRegion : classes.srOnly}
      >
        {announcement}
      </Typography>
    </>
  );
}

function ConnectionReadFailure({ view }: { view: ControlPlaneOnboardingView }) {
  return (
    <>
      <Typography role="alert" color="error">
        {view.error ?? 'Não foi possível carregar a atividade.'}
      </Typography>
      <Typography role="status" aria-live="polite" aria-atomic="true">
        {view.announcement}
      </Typography>
    </>
  );
}
